import { useEffect } from "react";
import { logger } from "@/utils/loggingUtils";

const SUPPRESSED_TOKENS = ["tokenHighlight", "Empty reason"];

/**
 * Suppresses known unhandled rejections from the Learnosity SDK.
 *
 * These are SDK-internal errors that surface as unhandled promise rejections
 * but do not impact the lesson experience. Without this, Datadog RUM would
 * flood with noise from the Learnosity Player.
 */
const useSuppressLearnosityRejections = (): void => {
  useEffect(() => {
    const handleLearnosityRejection = (event: PromiseRejectionEvent) => {
      const { reason } = event;
      const message =
        typeof reason === "string" ? reason : (reason?.message ?? "");
      const stack = typeof reason === "object" ? (reason?.stack ?? "") : "";

      const isLearnosityError =
        SUPPRESSED_TOKENS.some(token => message.includes(token)) ||
        SUPPRESSED_TOKENS.some(token => stack.includes(token));

      if (!isLearnosityError) return;

      logger.warn("[Learnosity] suppressed unhandled rejection", {
        message,
        reason
      });
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleLearnosityRejection);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleLearnosityRejection
      );
    };
  }, []);
};

export default useSuppressLearnosityRejections;
